// src/pages/team/TeamAutomationEventsPage.tsx

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useTeam, useI18n } from '@react/hooks';
import {
  Card,
  CardContent,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import {
  automationEventTypeMap,
  automationEventCategoryMap,
  getAutomationEventCategory,
  getTeamName,
  formatDateTime,
} from '@/features/team/logic';
import { TeamSectionNav } from '@/features/team/TeamSectionNav';

export const TeamAutomationEventsPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTeam();
  const { t, locale } = useI18n();

  const [events, setEvents] = useState<any[]>([]);

  const loadEvents = useCallback(async () => {
    if (!id) return;
    try {
      const res = await store.ListRoleAutomationEvents(id);
      setEvents(res.events?.events ?? []);
    } catch { /* store handles */ }
  }, [id, store]);

  useEffect(() => {
    if (id) store.GetTeam(id);
    loadEvents();
  }, [id, loadEvents]);

  const teamName = getTeamName(store.currentItem) || t('team.title');

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/teams" className="text-muted-foreground hover:text-foreground">{t('team.list')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/teams/${id}`} className="text-muted-foreground hover:text-foreground">{teamName}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('team.nav.automationLog')}</span>
      </nav>
      <TeamSectionNav teamId={id ?? ''} group="people" active="automation-log" />


      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('team.automationTitle')}</h1>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {/* Legend */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{t('team.automationCategories')}</span>
            <Badge variant="primary" size="sm">{t('team.automationCategory.schedule')}</Badge>
            <Badge variant="warning" size="sm">{t('team.automationCategory.delegation')}</Badge>
            <Badge variant="secondary" size="sm">{t('team.automationCategory.temporary')}</Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('team.automationCol.event')}</TableHead>
              <TableHead>{t('team.automationCol.source')}</TableHead>
              <TableHead>{t('team.automationCol.role')}</TableHead>
              <TableHead>{t('team.automationCol.from')}</TableHead>
              <TableHead>{t('team.automationCol.to')}</TableHead>
              <TableHead>{t('team.automationCol.details')}</TableHead>
              <TableHead>{t('team.automationCol.occurredAt')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">{t('common.loading')}</TableCell>
              </TableRow>
            ) : events.length === 0 ? (
              <TableEmpty colSpan={7}>
                <span className="table-empty-message">{t('team.noAutomationEvents')}</span>
                <p className="mt-1 text-sm text-muted-foreground">{t('team.automationDescription')}</p>
              </TableEmpty>
            ) : (
              events.map((ev: any, i: number) => {
                const eventType = ev.event_type || '';
                const category = getAutomationEventCategory(eventType);
                const eventLabelKey = automationEventTypeMap[eventType];
                const categoryVariant = automationEventCategoryMap[category] || 'secondary';

                return (
                  <TableRow key={ev.entity?.id || i}>
                    <TableCell>
                      <Badge variant={categoryVariant as any} size="sm">
                        {eventLabelKey ? t(eventLabelKey) : eventType.replace('ROLE_AUTOMATION_EVENT_TYPE_', '')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs font-mono text-muted-foreground">{ev.source?.id?.slice(0, 8) || '--'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{ev.role?.name || ev.role?.id || '--'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">{ev.from_assignee?.display_name || '--'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">{ev.to_assignee?.display_name || '--'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground truncate max-w-xs block">{ev.details || '--'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">{formatDateTime(ev.occurred_at, locale)}</span>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default TeamAutomationEventsPage;
